/**
 * Auto Structure Transformation Engine
 * Infers missing cinematic details from scene context to enrich prompt output.
 */

import { CanvasElement, GlobalSettings } from './engineTypes';

export interface InferredLayer {
  category: string;
  detail: string;
}

interface SceneContext {
  timeOfDay: 'dawn' | 'morning' | 'noon' | 'afternoon' | 'golden_hour' | 'sunset' | 'dusk' | 'night' | 'unknown';
  isIndoor: boolean;
  isOutdoor: boolean;
  mood: string;
  weather: string;
  environment: string;
}

const TIME_PATTERNS: [RegExp, SceneContext['timeOfDay']][] = [
  [/\b(dawn|فجر|sunrise|شروق)\b/i, 'dawn'],
  [/\b(morning|صباح|صبح)\b/i, 'morning'],
  [/\b(noon|ظهر|midday)\b/i, 'noon'],
  [/\b(afternoon|عصر|بعد الظهر)\b/i, 'afternoon'],
  [/\b(golden.?hour|الساعة الذهبية|magic.?hour)\b/i, 'golden_hour'],
  [/\b(sunset|غروب|مغرب)\b/i, 'sunset'],
  [/\b(dusk|twilight|شفق)\b/i, 'dusk'],
  [/\b(night|ليل|بالليل|midnight|نص الليل|مسا)\b/i, 'night'],
];

const INDOOR_PATTERNS = /\b(room|studio|apartment|office|مكتب|غرفة|استوديو|kitchen|مطبخ|bathroom|حمام|hallway|interior|indoor|warehouse|cafe|كافيه|restaurant|مطعم|museum|متحف|gallery)\b/i;
const OUTDOOR_PATTERNS = /\b(street|شارع|garden|حديقة|park|beach|بحر|شاطئ|mountain|جبل|forest|غابة|desert|صحراء|city|مدينة|outdoor|roof|سطح|sky|سما|field|حقل|river|نهر|ocean|محيط|jungle)\b/i;

const MOOD_PATTERNS: [RegExp, string][] = [
  [/\b(dark|مظلم|scary|مرعب|horror|رعب|grim|قاتم)\b/i, 'dark and foreboding'],
  [/\b(romantic|رومانسي|love|حب|tender|عاطفي)\b/i, 'romantic and tender'],
  [/\b(epic|ملحمي|grand|عظيم|heroic|بطولي)\b/i, 'epic and grand'],
  [/\b(peaceful|هادي|calm|سكينة|serene|هادئ)\b/i, 'peaceful and serene'],
  [/\b(dramatic|درامي|intense|حاد|tense|متوتر)\b/i, 'dramatic and intense'],
  [/\b(mysterious|غامض|enigma|سري|eerie|مخيف)\b/i, 'mysterious and enigmatic'],
  [/\b(joyful|فرحان|happy|سعيد|cheerful|مبهج)\b/i, 'joyful and uplifting'],
  [/\b(melancholy|حزين|sad|كئيب|nostalgic|حنين)\b/i, 'melancholic and nostalgic'],
];

const WEATHER_PATTERNS: [RegExp, string][] = [
  [/\b(rain|مطر|rainy|ممطر|storm|عاصفة|thunder|رعد)\b/i, 'rain-soaked atmosphere'],
  [/\b(snow|ثلج|snowy|winter|شتاء|blizzard)\b/i, 'snow-covered, cold winter air'],
  [/\b(fog|ضباب|mist|غيم|haze|شبورة)\b/i, 'misty, fog-laden atmosphere'],
  [/\b(wind|رياح|windy|عاصف)\b/i, 'wind-swept dynamic atmosphere'],
  [/\b(sunny|مشمس|clear|صافي)\b/i, 'clear, bright atmosphere'],
];

function analyzeScene(sceneDescription?: string): SceneContext {
  const ctx: SceneContext = {
    timeOfDay: 'unknown',
    isIndoor: false,
    isOutdoor: false,
    mood: '',
    weather: '',
    environment: '',
  };
  if (!sceneDescription?.trim()) return ctx;
  const s = sceneDescription;

  // Time
  for (const [pat, time] of TIME_PATTERNS) {
    if (pat.test(s)) { ctx.timeOfDay = time; break; }
  }

  // Location
  ctx.isIndoor = INDOOR_PATTERNS.test(s);
  ctx.isOutdoor = OUTDOOR_PATTERNS.test(s);
  if (!ctx.isIndoor && !ctx.isOutdoor) ctx.isOutdoor = true; // default outdoor

  // Mood
  for (const [pat, mood] of MOOD_PATTERNS) {
    if (pat.test(s)) { ctx.mood = mood; break; }
  }

  // Weather
  for (const [pat, w] of WEATHER_PATTERNS) {
    if (pat.test(s)) { ctx.weather = w; break; }
  }

  return ctx;
}

const TIME_COLOR_TEMP: Record<string, string> = {
  dawn: 'warm golden-pink dawn light, color temperature ~3500K',
  morning: 'fresh morning daylight, color temperature ~5500K',
  noon: 'neutral overhead midday light, color temperature ~6500K',
  afternoon: 'warm afternoon light, color temperature ~5000K',
  golden_hour: 'rich golden hour warmth, color temperature ~3000K',
  sunset: 'deep warm sunset glow, orange and magenta sky, color temperature ~2500K',
  dusk: 'cool blue twilight, fading light, color temperature ~7500K',
  night: 'cool moonlit ambiance, deep blue tones, color temperature ~8000K',
};

/**
 * Infer cinematic layers that the user didn't explicitly set.
 * Returns enrichment details to append to the prompt.
 */
export function inferCinematicLayers(
  elements: CanvasElement[],
  globalSettings: GlobalSettings,
  sceneDescription?: string
): InferredLayer[] {
  const layers: InferredLayer[] = [];
  const ctx = analyzeScene(sceneDescription);
  const hasLights = elements.some(e => e.type === 'light');
  const hasCameras = elements.some(e => e.type === 'camera');
  const hasCharacters = elements.some(e => e.type === 'character');

  // Time-based color temperature inference
  if (ctx.timeOfDay !== 'unknown' && TIME_COLOR_TEMP[ctx.timeOfDay]) {
    layers.push({ category: 'Lighting', detail: TIME_COLOR_TEMP[ctx.timeOfDay] });
  }

  // Mood inference
  if (ctx.mood) {
    layers.push({ category: 'Mood', detail: ctx.mood });
  }

  // Weather atmosphere
  if (ctx.weather) {
    layers.push({ category: 'Atmosphere', detail: ctx.weather });
  }

  // Indoor ambient inference
  if (ctx.isIndoor && !hasLights) {
    layers.push({ category: 'Environment', detail: 'interior ambient bounce lighting with soft shadows' });
  }

  // Night scene without lights → moonlight
  if (ctx.timeOfDay === 'night' && !hasLights) {
    layers.push({ category: 'Lighting', detail: 'natural moonlight with deep blue ambient fill' });
  }

  // Auto depth-of-field suggestion for character close-ups
  if (hasCharacters && hasCameras) {
    const cam = elements.find(e => e.type === 'camera')!;
    const char = elements.find(e => e.type === 'character')!;
    const dx = cam.x - char.x;
    const dy = cam.y - char.y;
    const dist = Math.sqrt(dx * dx + dy * dy);
    if (dist < 20 && cam.properties.dof !== 'very_shallow' && cam.properties.dof !== 'razor_thin') {
      layers.push({ category: 'Focus', detail: 'intimate close-up warranting shallow depth of field for subject isolation' });
    }
  }

  // If no character but scene described → environmental focus
  if (!hasCharacters && sceneDescription?.trim()) {
    layers.push({ category: 'Composition', detail: 'environmental focus, landscape-centric framing' });
  }

  // Golden hour auto VFX
  if (ctx.timeOfDay === 'golden_hour' && globalSettings.godRaysGlobal < 20) {
    layers.push({ category: 'VFX', detail: 'soft volumetric golden rays filtering through atmosphere' });
  }

  // Rain → reflections
  if (ctx.weather.includes('rain') && globalSettings.surfaceClarity < 60) {
    layers.push({ category: 'Detail', detail: 'wet reflective surfaces, puddle reflections, glistening textures' });
  }

  // Night + city → neon reflections
  if (ctx.timeOfDay === 'night' && ctx.isOutdoor && /\b(city|مدينة|street|شارع|urban)\b/i.test(sceneDescription || '')) {
    layers.push({ category: 'Atmosphere', detail: 'urban night ambiance, neon reflections on wet asphalt' });
  }

  return layers;
}
